import { FC, memo, useCallback, useEffect, useMemo } from 'react'
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import {
  CrewTable,
  SelectCheckbox,
} from 'components/elements/crewTable/crewTable'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { Region, SettingKeyType } from '@crew/enums/app'
import BaselineDelete from '~icons/ic/baseline-delete'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useTranslation } from '@crew/modules/i18n'
import { useModal } from 'components/layouts/modal/useModal'
import { useToast } from 'hooks/useToast'
import { useState } from 'react'
import { useAppSelector } from 'states/hooks'
import { EntityType } from '@crew/enums/domain'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useProjectPermissions } from '@crew/hooks'
import { usePresenceState, useUserSetting } from '@crew/states'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import qs from 'qs'
import _ from 'lodash'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetProjectMembersQuery } from '@crew/apis/project/projectApis'
import { DEFAULT_PAGING_PARAMS } from 'configs/constants'
import { getParamAsString } from 'utils'
import {
  CrewUserPresence,
  UserPresenceState,
} from 'components/elements/crewUserPresence/crewUserPresence'
import { CrewPresenceStateIconSize } from 'components/elements/crewPresenceStateIcon/crewPresenceStateIcon'
import { GetProjectMembersRequest } from '@crew/apis/project/models/getProjectMembers/request'
import { ProjectMembers } from '@crew/apis/project/models/getProjectMembers/response'
import { useGroupChatMemberListContext } from '../../../../useGroupChatMemberList'
import { useMemberTable } from './useMemberTable'

type UserPresenceRenderProps = {
  userId: string
}

// ユーザー在席状態を表示するコンポーネント
const UserPresenceRender = memo((props: UserPresenceRenderProps) => {
  const { t } = useTranslation()

  // ユーザー在席状態
  const [userPresenceState, setUserPresenceState] =
    useState<UserPresenceState>()

  // ユーザー在席状態の変更検知時、ユーザー在席状態を更新
  const handleUserPresenceStateChange = useCallback(
    (userPresence: UserPresenceState) => {
      setUserPresenceState(userPresence)
    },
    []
  )

  // 在席状態IDから在席状態を取得
  const presenceState = usePresenceState(userPresenceState?.presenceStateId)

  // 在席に表示するテキスト
  const textPresenceState = useMemo(() => {
    // 在席状態名（取得できない場合は「不明」を表示）
    const presenceStateName = presenceState?.name ?? t('label.unknown')

    return !userPresenceState?.presenceStateMessage
      ? presenceStateName // メッセージがない場合：在席状態のみ表示
      : `${presenceStateName}/${userPresenceState.presenceStateMessage}` // メッセージがある場合：「在席状態 / メッセージ」の形式で表示
  }, [presenceState, t, userPresenceState])

  return (
    <div className="flex flex-row items-center gap-1">
      <span className="shrink-0">
        <CrewUserPresence
          userId={props.userId}
          onChange={handleUserPresenceStateChange}
          size={CrewPresenceStateIconSize.base}
        />
      </span>
      <p>{textPresenceState}</p>
    </div>
  )
})

export const MemberTable: FC = memo(() => {
  const { t } = useTranslation()
  const { success } = useToast()
  const [showApiErrors] = useShowApiErrors()

  const { projectId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const params = qs.parse(searchParams.toString())

  const { setSelectedUserIds } = useGroupChatMemberListContext()

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [currentMember, setCurrentMember] = useState<ProjectMembers>()

  const { deleteGroupMember, isLoadingDeleteGroupMember } = useMemberTable()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  // ユーザー設定からページングに関する項目を取得
  const defaultListDisplayNumber = useUserSetting(
    SettingKeyType.ListDisplayNumber,
    DEFAULT_PAGING_PARAMS.pageSize
  )

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: Number(
        getParamAsString('pageIndex', params) ?? DEFAULT_PAGING_PARAMS.pageIndex
      ),
      pageSize: Number(
        getParamAsString('pageSize', params) ?? defaultListDisplayNumber
      ),
    }),
    [defaultListDisplayNumber, params]
  )

  const groupMemberEventMessage = useAppSelector(
    (state) => state.app.projectMemberEventMessage
  )

  const requestParams: GetProjectMembersRequest | undefined = projectId
    ? {
        projectId: projectId,
        keyword: getParamAsString('keyword', params) ?? '',
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      }
    : undefined

  // メンバーのリストを取得するAPIを呼び出す
  const { data: getGroupMembersResult, refetch: getGroupMembersRefetch } =
    useGetProjectMembersQuery(requestParams ?? skipToken)

  const groupMembers = useMemo(
    () => getGroupMembersResult?.projectMembers ?? [],
    [getGroupMembersResult?.projectMembers]
  )

  const totalCount = useMemo(
    () => getGroupMembersResult?.totalCount ?? 0,
    [getGroupMembersResult?.totalCount]
  )

  // 選択されたメンバーを取得
  const selectedGroupMembers = useMemo(() => {
    if (!getGroupMembersResult?.projectMembers) return []

    return getGroupMembersResult.projectMembers.filter((member) => {
      return rowSelection[member.userId]
    })
  }, [getGroupMembersResult?.projectMembers, rowSelection])

  // メンバーリストを再取得し、選択状態をクリアする
  useEffect(() => {
    if (!projectId) return

    getGroupMembersRefetch()

    return () => {
      setSelectedUserIds([])
      setRowSelection({})
    }
  }, [
    getGroupMembersRefetch,
    groupMemberEventMessage,
    projectId,
    setSelectedUserIds,
  ])

  const { hasPrjMemberDeletePermission } = useProjectPermissions(
    EntityType.Project,
    projectId
  )

  const [columnVisibility, setColumnVisibility] = useState({})
  const [columnPinning] = useState({
    left: ['select'],
    right: ['action'],
  })

  // 確認ダイアログ用
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()
  const [confirmMessage, setConfirmMessage] = useState('')

  // メンバーを削除時の確認ダイアログを開く
  const handleOpenConfirmDeleteMemberButtonClick = useCallback(
    (member: ProjectMembers) => {
      setCurrentMember(member)
      setConfirmMessage(t('message.general.confirmMessage.delete'))
      openConfirmDialog()
    },
    [openConfirmDialog, t]
  )

  // リスト内のメンバー削除ボタンクリック時の処理
  const handleDeleteMemberButtonClick = useCallback(
    async (member: ProjectMembers) => {
      try {
        // 処理不可な場合、ダイアログを閉じて終了
        if (!member || !projectId) {
          closeConfirmDialog()
          return
        }

        // プロジェクトメンバー削除処理実行
        await deleteGroupMember(projectId, member.userId)

        // 選択状態をリセット
        const selection = Object.keys(rowSelection).reduce((obj, key) => {
          if (member.userId !== key) {
            obj[key] = rowSelection[key]
          }
          return obj
        }, {} as RowSelectionState)
        setRowSelection(selection)

        success(t('message.groupChat.groupChatMemberDeleted'))
      } catch (err) {
        showApiErrors(err)
      }
      closeConfirmDialog()
    },
    [
      closeConfirmDialog,
      projectId,
      deleteGroupMember,
      rowSelection,
      success,
      t,
      showApiErrors,
    ]
  )

  // 確認ダイアログの「OK」ボタンクリック時の処理
  const handleConfirmDialogPermitButtonClick = useCallback(() => {
    if (currentMember) {
      // メンバー削除処理を実行
      return handleDeleteMemberButtonClick(currentMember)
    }
  }, [handleDeleteMemberButtonClick, currentMember])

  // ユーザー表示のrender
  const renderUserColumnByState = useCallback((member: ProjectMembers) => {
    return (
      <CrewUserAvatar
        userId={member.userId}
        displayName={member.user.displayName}
        size={CrewAvatarSize.xs}
        showLabel={true}
        cacheValue={member.user.id + member.user.version}
      />
    )
  }, [])

  // https://github.com/TanStack/table/discussions/3899
  // https://github.com/TanStack/table/discussions/3619
  // https://github.com/infonomic/remix.infonomic.io/blob/d3a7f628d3ad6e1e80cc80d4ac72db74da90e8d6/app/routes/admin%2B/users.tsx#L116
  // Func handle change pagination
  const handlePaginationListChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      let values: PaginationState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(pagination)
      } else {
        values = updaterOrValue
      }

      const newParams = {
        ...params,
        pageIndex: values.pageIndex,
        pageSize: values.pageSize,
      }

      // paramsが変わっていない場合はnavigateしない
      if (_.isEqual(params, newParams)) return

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })

      navigate(`?${newQueryString}`)
    },
    [navigate, pagination, params]
  )

  const pageCount = Math.ceil((totalCount ?? 0) / pagination.pageSize)

  const columns = useMemo<ColumnDef<ProjectMembers>[]>(() => {
    const tableColumns: ColumnDef<ProjectMembers>[] = [
      // 行選択チェックボックス
      {
        id: 'select',
        accessorKey: 'select',
        header: ({ table }) => (
          <SelectCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="flex flex-1 justify-center">
            <SelectCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        size: 80,
        minSize: 50,
        enableSorting: false,
        meta: {
          align: 'center',
        },
      },
      // ユーザー情報
      {
        id: 'user',
        accessorKey: 'user',
        header: () => t('label.user'),
        cell: ({ row }) => {
          return (
            <div className="truncate">
              {renderUserColumnByState(row.original)}
            </div>
          )
        },
        size: 200,
        minSize: 50,
        enableSorting: false,
      },
      // 在席
      {
        id: 'presence',
        accessorKey: 'presence',
        header: () => t('label.presence'),
        cell: ({ row }) => {
          const member = row.original
          return (
            <div className="truncate">
              <UserPresenceRender userId={member.userId} />
            </div>
          )
        },
        size: 200,
        minSize: 50,
        enableSorting: false,
      },
    ]

    tableColumns.push(
      {
        id: 'registeredDate',
        accessorKey: 'registeredDate',
        header: () => t('label.registeredDate'),
        cell: ({ row }) => {
          return (
            <div className="truncate">
              {t('format.datetime', {
                value: row.original.createdAt,
                timeZone: defaultUserProfileRegion,
              })}
            </div>
          )
        },
        size: 160,
        minSize: 50,
        enableSorting: false,
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: '',
        cell: ({ row }) => (
          <div>
            {hasPrjMemberDeletePermission && (
              // 「プロジェクトメンバーの削除」権限を持っている場合　：表示する
              <div className="flex gap-2 pr-2 justify-end items-center">
                <span
                  className="cursor-pointer"
                  onClick={() =>
                    handleOpenConfirmDeleteMemberButtonClick(row.original)
                  }
                >
                  <BaselineDelete width={24} height={24} />
                </span>
              </div>
            )}
          </div>
        ),
        size: 80,
        minSize: 50,
        enableSorting: false,
      }
    )

    return tableColumns
  }, [
    t,
    renderUserColumnByState,
    defaultUserProfileRegion,
    hasPrjMemberDeletePermission,
    handleOpenConfirmDeleteMemberButtonClick,
  ])

  const tableOptions: TableOptions<ProjectMembers> = {
    data: groupMembers,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
      columnVisibility,
      columnPinning,
      rowSelection: rowSelection,
    },
    getRowId: (row) => row.userId,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationListChange,
    manualPagination: true,
    enableRowSelection: true,
    maxMultiSortColCount: 2,
    pageCount,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
    },
  }

  // 選択された行の元のデータを親コンポーネントに設定する
  useEffect(() => {
    setSelectedUserIds(
      selectedGroupMembers.map((selectedMember) => {
        return selectedMember.userId
      })
    )
  }, [selectedGroupMembers, setSelectedUserIds])

  return (
    <div className="overflow-y-auto">
      {/* メンバー一覧 */}
      <CrewTable tableOptions={tableOptions} showColumnSelector={false} />
      {/* メンバー削除時確認ダイアログ */}
      <CrewConfirmDialog
        isOpen={isConfirmDialogOpen}
        message={confirmMessage}
        onPermitButtonClick={handleConfirmDialogPermitButtonClick}
        onCancelButtonClick={closeConfirmDialog}
        permitButtonDisabled={isLoadingDeleteGroupMember}
      />
    </div>
  )
})
